import { EditorState, Plugin, PluginKey, Transaction } from 'prosemirror-state'
import { EditorView } from 'prosemirror-view'
import { schema } from '../Schema'
import { Slice } from 'prosemirror-model'
import { NodeSelection } from 'prosemirror-state'
import { flatten } from 'lodash'

export interface State extends Config {
  from: number
  to: number
}

export interface Config {
  splitOn: string[]
  validatePill: (pill: string) => boolean
}

const PillsPluginKey = new PluginKey<State>('PillsPlugin')

const createPill = (view: EditorView<any>, validatePill: (pill: string) => boolean) => {
  const text = view.state.selection.$head.parent.textContent
  const trimmedText = text.trim()

  if (trimmedText === '' || !validatePill(trimmedText)) return

  const to = view.state.selection.to
  const from = to - text.length

  if (from > to || from < 0 || to < 0) return

  const transaction = view.state.tr.replaceWith(from, to, [schema.nodes.pill.create({ text: trimmedText })])
  view.dispatch(transaction)
}

const createPills = (
  view: EditorView<any>,
  { text, splitOn, validatePill }: { text: string; splitOn: string[]; validatePill: (pill: string) => boolean },
) => {
  const transaction = createPillsTransaction({ text, splitOn, state: view.state, tr: view.state.tr, validatePill })
  if (transaction) view.dispatch(transaction)
}

const createPillsTransaction = ({
  text,
  splitOn,
  state,
  tr,
  replace,
  validatePill,
}: {
  text: string
  splitOn: string[]
  state: EditorState<any>
  tr: Transaction<any>
  replace?: boolean
  validatePill: (pill: string) => boolean
}) => {
  const position = replace ? state.selection.from - text.length : state.selection.from
  const trimmedText = text.trim()
  if (trimmedText === '') return
  const pills = splitOn
    .reduce(
      (acc, char) => {
        return acc.flatMap((text) => {
          return text.split(char)
        })
      },
      [trimmedText],
    )
    .filter((p) => p !== '')

  const pillNodes = pills
    .filter((pill) => validatePill(pill))
    .map((pill) => schema.nodes.pill.create({ text: pill.trim() }))

  if (replace) return tr.replaceWith(position, position + text.length, pillNodes)

  return tr.insert(position, pillNodes)
}

class View {
  timeout: NodeJS.Timeout

  constructor(view: EditorView, { splitOn, validatePill }: Config) {
    this.timeout = setTimeout(() => {
      const { state } = view
      // Wait for the next tick so that the view is initialised
      const text = state.doc.lastChild?.textContent
      if (text) {
        const tx = createPillsTransaction({
          text,
          splitOn,
          state,
          tr: state.tr,
          replace: true,
          validatePill,
        })
        if (tx) view.dispatch(tx)
      }
    }, 0)
  }

  update() {}

  destroy() {
    clearTimeout(this.timeout)
  }
}

export const PillsPlugin = (
  props: Config = {
    splitOn: [','],
    validatePill: (pill: string) => true,
  },
) =>
  new Plugin({
    key: PillsPluginKey,
    props: {
      handleDOMEvents: {
        blur(view) {
          createPill(view, props.validatePill)

          return false
        },
      },
      handleKeyDown(view, event) {
        if (event.key === ' ') {
          if (view.state.doc.lastChild?.textContent === '') {
            // Don't allow a space to be the first character of the content
            return true
          }
          if (view.state.doc.lastChild?.textContent.endsWith('. ')) {
            // Handle double-space turned into ". "
            view.state.doc.descendants((node, pos) => {
              if (node.type === view.state.schema.nodes.text) {
                const tr = view.state.tr
                const text = node.textContent
                const newTextNode = view.state.schema.text(text.slice(0, text.length - 2))
                tr.setSelection(new NodeSelection(view.state.doc.resolve(pos)))
                view.dispatch(tr.replaceSelectionWith(newTextNode))
              }
              return true
            })
            return true
          }
        }

        if (flatten(['Tab', props.splitOn]).includes(event.key)) {
          if (view.state.selection.from === view.state.selection.to) {
            createPill(view, props.validatePill)
          }
          return true
        }

        if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault()
          return true
        }

        return false
      },
      // Used to replace text with pills when pasting
      handlePaste(view: EditorView, e: Event, slice: Slice) {
        const pastedText = slice.content?.firstChild?.textContent
        if (pastedText && props.splitOn.some((char) => pastedText?.includes(char))) {
          e.preventDefault()
          createPills(view, { text: pastedText, ...props })
          return true
        }
        return false
      },
    },
    // Used to replace text with pills when text gets inserted through the snippets bottom tray/toolbar
    appendTransaction: (transactions, _oldState, newState) => {
      const { tr } = newState
      const text = newState.doc.lastChild?.textContent
      const insertedFromToolbar = transactions.some((t) => t.getMeta('insertedFromToolbar'))
      const insertedFromMention = transactions.some((t) => t.getMeta('insertedFromMention'))
      if (text && (insertedFromToolbar || insertedFromMention)) {
        const transaction = createPillsTransaction({
          text,
          state: newState,
          tr,
          replace: true,
          ...props,
        })
        return transaction
      }
    },
    view: (view) => new View(view, props),
  })
